import {
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { User, UserRole } from '../../types/api';
import { getErrorMessage } from '../../utils/errorUtils';
import ErrorAlert from '../Common/ErrorAlert';
import LoadingSpinner from '../Common/LoadingSpinner';

interface InterviewersTableProps {
  cycleId: string;
}

const InterviewersTable: React.FC<InterviewersTableProps> = ({ cycleId }) => {
  const queryClient = useQueryClient();
  const { lang } = useDirection();
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addInterviewer, setAddInterviewer] = useState<User | null>(null);
  const [addCandIds, setAddCandIds] = useState<string[]>([]);
  const [addError, setAddError] = useState('');
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  const { data: interviewAssignments, isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['interviewAssignments', cycleId],
    queryFn: () => apiService.getInterviewAssignmentsByCycle(cycleId),
    enabled: !!cycleId,
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiService.getUsers(),
  });

  const { data: cycleStatus } = useQuery({
    queryKey: ['cycleStatus', cycleId],
    queryFn: () => apiService.getCycleStatus(cycleId),
    enabled: !!cycleId,
  });

  const uploadMutation = useMutation({
    mutationFn: ({ cycleId, file }: { cycleId: string; file: File }) =>
      apiService.uploadInterviewsCSV(cycleId, file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['interviewAssignments'] });
      refetch();
      setSuccessMsg(data.message);
      setError('');
    },
    onError: (err: any) => {
      setError(getErrorMessage(err));
      setSuccessMsg('');
    },
  });

  const assignMutation = useMutation({
    mutationFn: ({ userId, candidateIds }: { userId: string; candidateIds: string[] }) =>
      apiService.assignInterviewerToCandidates(cycleId, { userId, candidateIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviewAssignments'] });
      refetch();
      setAddOpen(false);
      setAddInterviewer(null);
      setAddCandIds([]);
      setAddError('');
      setSuccessMsg(tr('intv.addedSuccess', lang));
    },
    onError: (err: any) => setAddError(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) =>
      apiService.removeInterviewAssignmentsByInterviewer(cycleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviewAssignments'] });
      refetch();
      setDeleteUserId(null);
      setSuccessMsg(tr('intv.removedSuccess', lang));
    },
    onError: (err: any) => {
      setError(getErrorMessage(err));
      setDeleteUserId(null);
    },
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !cycleId) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError(tr('intv.selectCsv', lang));
      return;
    }
    uploadMutation.mutate({ cycleId, file });
    e.target.value = '';
  };

  const handleAddSubmit = () => {
    if (!addInterviewer || addCandIds.length === 0) {
      setAddError(tr('intv.selectBoth', lang));
      return;
    }
    assignMutation.mutate({ userId: addInterviewer.id, candidateIds: addCandIds });
  };

  // Group assignments by interviewer
  const interviewersMap = new Map<string, { id: string; user: any; role: string; assignments: any[] }>();
  if (interviewAssignments) {
    interviewAssignments.forEach(a => {
      const userId = a.user?.id;
      if (userId) {
        if (!interviewersMap.has(userId)) {
          interviewersMap.set(userId, { id: userId, user: a.user, role: UserRole.INTERVIEWER, assignments: [] });
        }
        interviewersMap.get(userId)!.assignments.push(a.candidate);
      }
    });
  }
  const interviewers = Array.from(interviewersMap.values());
  const assignmentsMap = new Map<string, any[]>();
  interviewers.forEach(i => assignmentsMap.set(i.id, i.assignments));

  const allCandidates: any[] = cycleStatus?.teams?.flatMap((t: any) => t.candidates ?? []) ?? [];

  const columns: GridColDef[] = [
    {
      field: 'userEmail', headerName: tr('intv.col.email', lang), width: 240,
      renderCell: (p: GridRenderCellParams) => p.row.user?.email || '-',
    },
    {
      field: 'userName', headerName: tr('intv.col.name', lang), width: 180,
      renderCell: (p: GridRenderCellParams) =>
        p.row.user ? `${p.row.user.firstName} ${p.row.user.lastName}` : '-',
    },
    {
      field: 'role', headerName: tr('intv.col.role', lang), width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <Chip label={p.value} color="warning" size="small" />
      ),
    },
    {
      field: 'assignedCandidates',
      headerName: tr('intv.col.candidates', lang),
      width: 300,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams) => {
        const candidates = assignmentsMap.get(p.row.id) || [];
        if (candidates.length === 0) {
          return <Typography variant="body2" color="text.secondary">{tr('intv.noAssignments', lang)}</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5, alignItems: 'center' }}>
            {candidates.map((c: any, i: number) => (
              <Chip
                key={c?.id || i}
                label={`${c?.teamNumber ?? '?'}-${c?.candidateNumber ?? '?'}`}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.75rem', height: '24px' }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 60,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label={tr('intv.removeAll', lang)}
          onClick={() => setDeleteUserId(row.id)}
          color="error"
        />,
      ],
    },
  ];

  if (isLoading) return <LoadingSpinner message={tr('intv.loading', lang)} />;
  if (queryError) return <ErrorAlert error={queryError} title={tr('intv.errorLoad', lang)} />;

  const gridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': { bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748B' },
    '& .MuiDataGrid-row:hover': { bgcolor: '#F8FAFC' },
    '& .MuiDataGrid-cell': { borderBottom: '1px solid #F1F5F9', fontSize: '0.875rem', display: 'flex', alignItems: 'center', lineHeight: 'unset !important', maxHeight: 'none !important', whiteSpace: 'normal' },
    '& .MuiDataGrid-row': { maxHeight: 'none !important' },
    '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E2E8F0' },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  };

  return (
    <div>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0F172A' }}>
            {tr('cd.tab.interviews', lang)}
            <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#94A3B8', fontSize: '0.85rem' }}>
              {interviewers.length} · {interviewAssignments?.length ?? 0} {tr('intv.assignments', lang)}
            </Typography>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            size="small"
            onClick={() => { setAddOpen(true); setAddError(''); }}
          >
            {tr('intv.addAssignment', lang)}
          </Button>
          <input accept=".csv" style={{ display: 'none' }} id="interviews-csv-upload-input" type="file" onChange={handleFileUpload} />
          <Tooltip title={tr('intv.csvHint', lang)} arrow placement="top">
            <label htmlFor="interviews-csv-upload-input">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />} size="small" disabled={uploadMutation.isPending}>
                {uploadMutation.isPending ? tr('common.uploading', lang) : tr('intv.uploadCsv', lang)}
              </Button>
            </label>
          </Tooltip>
          <Button variant="outlined" startIcon={<RefreshIcon />} size="small" onClick={() => refetch()}>
            {tr('common.refresh', lang)}
          </Button>
        </Box>
      </Box>

      {interviewers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, border: '2px dashed #E2E8F0', borderRadius: 3, color: '#94A3B8' }}>
          <Typography>{tr('intv.noneAssigned', lang)}</Typography>
        </Box>
      ) : (
        <Paper sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
          <DataGrid
            rows={interviewers}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            getRowHeight={() => 'auto'}
            sx={gridSx}
          />
        </Paper>
      )}

      {/* Add Assignment Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{tr('intv.addTitle', lang)}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {addError && <Alert severity="error">{addError}</Alert>}
          <Autocomplete
            options={users ?? []}
            getOptionLabel={u => `${u.firstName} ${u.lastName} (${u.email})`}
            value={addInterviewer}
            onChange={(_, v) => setAddInterviewer(v)}
            renderInput={p => <TextField {...p} label={tr('intv.interviewer', lang)} size="small" />}
          />
          <Autocomplete
            multiple
            options={allCandidates}
            getOptionLabel={(c: any) => `#${c.candidateNumber} (Team ${c.teamNumber})`}
            value={allCandidates.filter((c: any) => addCandIds.includes(c.id))}
            onChange={(_, v) => setAddCandIds((v as any[]).map(c => c.id))}
            renderInput={p => <TextField {...p} label={tr('intv.candidates', lang)} size="small" />}
            renderTags={(v, getProps) =>
              (v as any[]).map((c, i) => {
                const { key, ...tagProps } = getProps({ index: i }) as any;
                return <Chip key={c.id} label={`#${c.candidateNumber}`} size="small" {...tagProps} />;
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{tr('common.cancel', lang)}</Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={assignMutation.isPending}>
            {assignMutation.isPending ? tr('common.saving', lang) : tr('common.add', lang)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteUserId} onClose={() => setDeleteUserId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tr('intv.removeTitle', lang)}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {tr('intv.removeBody', lang)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserId(null)}>{tr('common.cancel', lang)}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteUserId && deleteMutation.mutate(deleteUserId)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? tr('intv.removing', lang) : tr('intv.remove', lang)}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InterviewersTable;
